/**
 * Login — Fase B migration to useZodForm (iter-208).
 *
 * Visible form fields (email/password) flow through useZodForm +
 * FormField + SubmitButton for unified validation, a11y and lock-on-
 * submit. Honeypot input and lang-picker pills are allowlisted
 * (legitimate raw HTML — not user-facing form fields).
 * Captcha + honeypot wiring is unchanged from iter-169zw.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LangContext";
import { formatApiError } from "../../lib/api";
import { useSecurityConfig } from "../../lib/useSecurityConfig";
import { toast } from "sonner";
import { LumfiWordmark } from "../../components/LumfiLogo";
import { TurnstileGate } from "../../components/TurnstileGate";
import { tx } from "../../i18n";
import { z } from "zod";
import { useZodForm, FormField, SubmitButton } from "../../components/form-ux";

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const { data: securityConfig } = useSecurityConfig();
  const captchaEnabled = !!securityConfig?.turnstile?.enabled;
  const isEs = lang === "es";

  // Non-form state (honeypot + captcha live outside useZodForm because
  // they are NOT user-visible fields).
  const [website, setWebsite] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [waitingForCaptcha, setWaitingForCaptcha] = useState(false);
  const [topError, setTopError] = useState("");
  const pendingSubmitRef = useRef(null);

  const LoginSchema = z.object({
    email: z
      .string()
      .trim()
      .min(1, tx(lang, "Email is required.", "El email es obligatorio.", "O e-mail é obrigatório."))
      .email(tx(lang, "Invalid email.", "Email inválido.", "E-mail inválido.")),
    password: z
      .string()
      .min(6, tx(lang, "Min 6 characters.", "Mínimo 6 caracteres.", "Mínimo 6 caracteres.")),
  });

  const form = useZodForm(LoginSchema, { email: "", password: "" }, { formId: "login" });

  const doLogin = async (creds) => {
    setTopError("");
    setWaitingForCaptcha(false);
    pendingSubmitRef.current = null;
    try {
      const user = await login(creds.email, creds.password, {
        website: creds.website,
        turnstile_token: creds.turnstileToken || undefined,
      });
      toast.success(tx(lang, "Welcome back", "¡Bienvenido de vuelta!", "¡Bem-vindo de volta!"));
      navigate(user.onboarded ? "/dashboard" : "/onboarding");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "email_not_verified") {
        navigate(`/verify-email-pending?email=${encodeURIComponent(creds.email)}`);
        return;
      }
      if (detail === "captcha_expired") {
        setTurnstileToken("");
        setWaitingForCaptcha(true);
        pendingSubmitRef.current = { ...creds, turnstileToken: "" };
        setTopError(formatApiError(detail));
        return;
      }
      setTopError(formatApiError(detail) || e.message);
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    if (captchaEnabled && !turnstileToken) {
      setTopError(tx(lang,
        "Security check in progress… try again in a second.",
        "Verificación de seguridad en curso… reintenta en un segundo.",
        "Verificação de segurança em andamento… tente novamente.",
      ));
      pendingSubmitRef.current = { email: values.email, password: values.password, website, turnstileToken: "" };
      setWaitingForCaptcha(true);
      return;
    }
    await doLogin({ email: values.email, password: values.password, website, turnstileToken });
  });

  const handleToken = (tok) => {
    setTurnstileToken(tok);
    if (tok) setTopError("");
    if (tok && pendingSubmitRef.current) {
      const queued = pendingSubmitRef.current;
      pendingSubmitRef.current = null;
      doLogin({ ...queued, turnstileToken: tok });
    }
  };

  useEffect(() => () => {
    pendingSubmitRef.current = null;
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10 safe-bottom">
      <div className="w-full max-w-md lumfi-rise-in">
        <div className="text-center mb-7 relative">
          <Link to="/" className="inline-flex items-center justify-center" aria-label="Lumfi">
            <LumfiWordmark height={40} />
          </Link>
          <div
            className="absolute top-0 right-0 inline-flex bg-white border border-slate-200 rounded-full p-0.5 shadow-sm"
            data-testid="login-lang-picker"
          >
            {["es", "en", "pt"].map((code) => (
              // design-system: ALLOW lang-picker toggle pill (3-state segmented control, no Button variant matches)
              <button /* design-system: ALLOW legacy-todo */
                key={code}
                type="button"
                onClick={() => setLang(code)}
                data-testid={`login-lang-${code}`}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${
                  lang === code
                    ? "bg-emerald-600 text-white"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>

        <Card className="p-6 md:p-8 rounded-2xl border-slate-200 shadow-sm">
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
            {isEs ? "Bienvenido de vuelta" : t("login_title")}
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">
            {isEs ? "Inicia sesión para seguir cuidando tu caja." : "Sign in to keep your cash on track."}
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form" noValidate>
            <FormField
              label={t("email")}
              name="email"
              required
              error={form.errors.email && (form.touched.email || form.submitCount > 0) ? form.errors.email : undefined}
            >
              <FormField.Input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="tú@ejemplo.com"
                data-testid="login-email-input"
                className="h-12 text-base rounded-xl"
                {...form.getFieldProps("email")}
              />
            </FormField>

            <FormField
              label={t("password")}
              name="password"
              required
              error={form.errors.password && (form.touched.password || form.submitCount > 0) ? form.errors.password : undefined}
            >
              <FormField.Input
                type="password"
                autoComplete="current-password"
                data-testid="login-password-input"
                className="h-12 text-base rounded-xl"
                {...form.getFieldProps("password")}
              />
              <Link
                to="/forgot-password"
                data-testid="login-forgot-link"
                className="block text-right text-xs text-emerald-700 hover:text-emerald-900 hover:underline mt-2 font-semibold"
              >
                {isEs ? "¿Olvidaste tu clave?" : lang === "pt" ? "Esqueceu sua senha?" : "Forgot password?"}
              </Link>
            </FormField>

            {topError && (
              <div
                data-testid="login-error"
                className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                role="alert"
              >
                {topError}
              </div>
            )}

            {/* design-system: ALLOW honeypot anti-bot input (must be hidden, raw) */}
            <input /* design-system: ALLOW legacy-todo */
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              data-testid="login-honeypot"
              className="absolute opacity-0 -left-[9999px] h-0 w-0"
            />

            <TurnstileGate action="login" lang={lang} onToken={handleToken} />

            <SubmitButton
              submitting={form.submitting || waitingForCaptcha}
              loadingText={
                waitingForCaptcha
                  ? (isEs ? "Reintentando…" : "Retrying…")
                  : t("loading")
              }
              testid="login-submit-btn"
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-base font-semibold"
            >
              {isEs ? "Iniciar sesión" : t("sign_in")}
            </SubmitButton>
          </form>

          <div className="mt-6 text-sm text-slate-600 text-center">
            {isEs ? "¿No tienes cuenta?" : t("no_account")}{" "}
            <Link to="/register" className="text-emerald-700 font-semibold hover:underline">
              {isEs ? "Crea una gratis" : t("sign_up")}
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
